import { Box, Grid, Typography } from '@mui/material';
import { SxProps } from '@mui/system';
import { styled } from '@mui/material/styles';
import { ReactNode } from 'react';
import { ColorShowcase } from 'shared/components/common/color-showcase';

type DetailColumnItem = {
  title: string;
  value?: string | null;
  tooltip?: ReactNode;
  showColorShowcase?: boolean;
  imageUrl?: string | null;
  imageSx?: SxProps;
};

export type DetailColumn = {
  title?: string;
  items: DetailColumnItem[];
};

export const DetailSection = ({ columns }: { columns: DetailColumn[] }) => {
  const columnSize = 12 / columns.length;
  return (
    <Grid container spacing={4} role="tabpanel">
      {columns.map((column, colIndex) => (
        <FlexGrid key={colIndex} item xs={12} md={columnSize} lg={columnSize}>
          {column.title ? (
            <Typography variant="h6" sx={{ mb: 2 }}>
              {column.title}
            </Typography>
          ) : null}
          {column.items.map((item, index) => (
            <ItemWrapper key={index}>
              <TitleRow>
                <Typography variant="subtitle2" color="secondary">
                  {item.title}
                </Typography>
                {item.tooltip ?? null}
              </TitleRow>
              {item.imageUrl ? (
                <Box
                  component="img"
                  src={item.imageUrl}
                  alt={item.title}
                  sx={{ maxWidth: 180, maxHeight: 80, objectFit: 'contain', ...item.imageSx }}
                />
              ) : item.showColorShowcase && item.value ? (
                <ColorShowcaseRow>
                  <ColorShowcase hex={item.value} />
                  <Typography variant="body1">{item.value}</Typography>
                </ColorShowcaseRow>
              ) : (
                <Typography variant="body1">{item.value ?? '-'}</Typography>
              )}
            </ItemWrapper>
          ))}
        </FlexGrid>
      ))}
    </Grid>
  );
};

const FlexGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const ItemWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const TitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minHeight: 24,
}));

const ColorShowcaseRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));
